import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import React, { FC, useCallback, useContext, useMemo } from 'react';

import { SEDUserRequestChannels } from '@/pages/UserAccount/UserRequests/lib';
import { DownloadButton } from '@/pages/UserAccount/UserRequests/ui';

import { ArtifactType, InvoiceStatus, UserRequestStatus, UserRequestChannel } from '@/shared/types';
import {
  Button,
  ButtonVariant,
  ConditionView,
  DangerousHTML,
  Icon,
  InformerProps,
  Link,
  LinkAsButton,
  ServiceRowCard,
  ServiceRowCardVariant,
  Status,
  Typography,
  useModal,
} from '@/shared/ui';

import { YesNoSelectOptions } from '@/components/Forms/constants';

import { useArtifactLoader } from '@/hooks/useArtifactLoader';

import { ROUTES_BY_SERVICE_CODE } from '@/router/config';

import { formatDate } from '@/helpers/dates';

import AuthContext from '@/contexts/Auth/AuthContext';

import { DeliveryType, SERVICE_CODES } from '@/lib/consts';

import { UserRequest } from '@/api/types';

import api from '@/api';

import { ChannelTag } from '../../ChannelTag/ChannelTag';
import { Truncatable } from '../../Truncatable/Truncatable';
import { BaseCard } from '../model/BaseCard/BaseCard';

import { CardAccordion } from './CardAccordion/CardAccordion';
import { ConfirmDeleteUserRequestModal, UserRequestExtendedArtifactModal } from './Modals';
import { Row, RowRenderer } from './RowRenderer';

export interface UserRequestCardProps {
  variant: ServiceRowCardVariant.USER_REQUEST;
  userRequest: UserRequest;
}

export const UserRequestCard: FC<UserRequestCardProps> = ({ userRequest, variant }) => {
  const {
    isOpen: isDeleteUserRequestModalOpen,
    openModal: openDeleteUserRequestModal,
    closeModal: closeDeleteUserRequestModal,
  } = useModal();
  const {
    isOpen: isExtendedArtifactModalOpen,
    openModal: openExtendedArtifactModal,
    closeModal: closeExtendedArtifactModal,
  } = useModal();
  const { storedLinks } = useContext(AuthContext);
  const { isArtifactLoading, downloadArtifact } = useArtifactLoader((artifactType) =>
    api.downloadUserRequestArtifact(userRequest.id, artifactType),
  );

  const showExternalDecision: boolean =
    (userRequest.channel === UserRequestChannel.EKG && !!userRequest.external_decision) ||
    userRequest.status === UserRequestStatus.PAYMENT;
  const isRequestInPaymentProgress: boolean =
    userRequest.status === UserRequestStatus.PAYMENT && userRequest?.invoice?.status !== InvoiceStatus.SUCCESS;

  const isArtifactExtended = useCallback(
    (artifactType: ArtifactType) => Object.keys(userRequest.artifacts_extend ?? {}).includes(artifactType),
    [userRequest],
  );

  const rows: Row[] = [
    {
      label: 'Номер запиту',
      value: userRequest.number_application,
      dataTestId: 'user-request-card-request-number',
    },
    {
      label: 'Дата створення',
      value: formatDate(userRequest.created_at),
      dataTestId: 'user-request-card-created-at',
    },
    {
      label: 'Дата оновлення',
      value: formatDate(userRequest.updated_at),
      dataTestId: 'user-request-card-updated-at',
    },
  ];

  const requestHasDelivery: boolean = !!userRequest.tracking_link && !!userRequest.tracking_number;
  const additionalRows: Row[] =
    (userRequest.service_code === SERVICE_CODES.EXTENDED_CRIMINAL_RECORD_EXTRACT ||
      userRequest.service_code === SERVICE_CODES.EXTRACT_REDELIVERY) &&
    Object.prototype.hasOwnProperty.call(userRequest, 'has_apostille')
      ? [
          ...(userRequest.delivery_status !== null
            ? [
                {
                  label: userRequest.delivery_type === 'personal' ? 'Статус видачі' : 'Статус доставки',
                  value: userRequest.delivery_status,
                  dataTestId: 'user-request-card-delivery_status',
                },
              ]
            : []),
          ...(requestHasDelivery
            ? [
                {
                  label: 'Номер ТТН Укрпошти',
                  value: <Link to={userRequest.tracking_link ?? ''}>{userRequest.tracking_number}</Link>,
                  dataTestId: 'user-request-card-ukrposhta-ttn-number',
                },
              ]
            : []),
          ...(userRequest.has_apostille !== null
            ? [
                {
                  label: 'Апостиль',
                  value: userRequest.has_apostille ? YesNoSelectOptions.YES.label : YesNoSelectOptions.NO.label,
                  dataTestId: 'user-request-card-apostille',
                },
              ]
            : []),
        ]
      : [];

  const renderInformer = useMemo<Nullable<InformerProps>>(() => {
    if (showExternalDecision) {
      return {
        icon: {
          name: userRequest.status_decorator?.message_icon ?? 'attention',
          color: userRequest.status_decorator?.message_color ?? 'red',
        },
        header: {
          text:
            userRequest.external_decision ||
            (userRequest.service_code === SERVICE_CODES.INSURANCE
              ? 'Вам необхідно оплатити страховку до кінця поточної доби, після закінчення цього терміну запит буде анульовано.'
              : 'Вам необхідно оплатити впродовж 14 днів, після закінчення цього терміну запит буде анульовано.'),
          color: userRequest.status_decorator?.message_color ?? 'red',
        },
        bgColor: 'white',
        borderColor: 'none',
      };
    }

    if (
      (userRequest.service_code === SERVICE_CODES.EXTENDED_CRIMINAL_RECORD_EXTRACT ||
        userRequest.service_code === SERVICE_CODES.EXTRACT_REDELIVERY) &&
      userRequest.tracking_link &&
      userRequest.tracking_number
    )
      return {
        icon: {
          name: 'attention',
          color: 'blue',
        },
        header: {
          text: 'Зверніть увагу!',
          color: 'blue',
        },
        text: {
          text: 'Ваш документ передано для доставки на Укрпошту. Ви можете легко відстежити статус відправлення за допомогою номера накладної (ТТН)',
          color: 'blue',
        },
        bgColor: 'white',
        borderColor: 'none',
      };
    if (
      userRequest.service_code === SERVICE_CODES.EXTENDED_CRIMINAL_RECORD_EXTRACT &&
      userRequest.delivery_type === DeliveryType.PERSONAL
    )
      return {
        icon: {
          name: 'attention',
          color: 'red',
        },
        header: {
          text: 'Зверніть увагу!',
          color: 'red',
        },
        text: {
          text: (
            <>
              <Typography variant="TEXT14" semibold>
                Графік та місце видачі витягів громадянам, які обрали самостійне отримання за адресою МВС:
              </Typography>
              <ul className="service-row-card__list">
                <li>м. Київ, вул. Богомольця 10, кабінет 7</li>
                <li>щоденно з понеділка до п&#39;ятниці з 10:00-11:00 та з 15:00-16:00</li>
              </ul>
            </>
          ),
          color: 'black',
        },
        bgColor: 'white',
        borderColor: 'none',
      };
    if (userRequest.reapplication_status === 'AWARD')
      return {
        icon: {
          name: 'attention',
          color: 'red',
        },
        header: {
          text: 'Зверніть увагу!',
          color: 'red',
        },
        text: {
          text: <DangerousHTML content={storedLinks?.serviceInformer?.reapplicationAward} />,
          color: 'black',
        },
      };
    return null;
  }, [showExternalDecision, userRequest]);

  return (
    <>
      <ConditionView if={isRequestInPaymentProgress}>
        <ConfirmDeleteUserRequestModal
          draftIdToDelete={userRequest.id}
          isModalOpen={isDeleteUserRequestModalOpen}
          closeModal={closeDeleteUserRequestModal}
        />
      </ConditionView>

      <BaseCard
        data-testid="user-request-card"
        data-request-id={userRequest.id}
        className={classNames('service-row-card', `service-row-card--${variant}`)}
        header={
          <div className="service-row-card__header">
            <div className="service-row-card__header-inner">
              <div data-testid="user-request-card-status" className="service-row-card__status-wrapper">
                <Status status={userRequest.status_decorator || userRequest.status} />
              </div>
              <div data-testid="user-request-card-channel-tag" className="service-row-card__channel-tag-wrapper">
                <ChannelTag channel={SEDUserRequestChannels[userRequest.channel]} />
              </div>
            </div>
            <div className="service-row-card__title-wrapper">
              <Truncatable
                text={
                  <>
                    <Typography data-testid="user-request-card-title" variant="H4">
                      {userRequest.title}
                    </Typography>
                    {userRequest.has_redelivery && <Typography>Повторна доставка витягу про несудимість</Typography>}
                  </>
                }
              />
            </div>
          </div>
        }
        body={
          <div className="service-row-card__body">
            <div className="service-row-card__meta-wrapper">
              <RowRenderer rows={rows} />
            </div>
            {additionalRows.length > 0 && (
              <div className="service-row-card__meta-wrapper">
                <RowRenderer rows={additionalRows} />
              </div>
            )}
          </div>
        }
        accordion={
          !!userRequest.parts?.length || userRequest.reapplication_orders.length ? (
            <CardAccordion
              items={[
                userRequest.parts?.length
                  ? {
                      label: <Typography className="service-row-card__accordion-label">Детальніше</Typography>,
                      children: (
                        <div
                          data-testid="user-request-card-accordion-content"
                          className="service-row-card__accordion-content"
                        >
                          {userRequest.parts.map((part) => (
                            <ServiceRowCard
                              key={part.id}
                              variant={ServiceRowCardVariant.USER_REQUEST_PART}
                              userRequestPart={part}
                              serviceCode={userRequest.service_code}
                            />
                          ))}
                        </div>
                      ),
                    }
                  : {},
                userRequest.reapplication_orders.length
                  ? {
                      label: <Typography className="service-row-card__accordion-label">Поновлення заяви</Typography>,
                      children: (
                        <div
                          data-testid="user-request-card-accordion-content"
                          className="service-row-card__accordion-content"
                        >
                          {userRequest.reapplication_orders.map((reapplication_order) => (
                            <UserRequestCard
                              key={reapplication_order.id}
                              variant={ServiceRowCardVariant.USER_REQUEST}
                              userRequest={reapplication_order}
                            />
                          ))}
                        </div>
                      ),
                    }
                  : {},
              ].filter((item) => !isEmpty(item))}
            />
          ) : null
        }
        informer={renderInformer}
        actions={[
          <ConditionView key="delete-request" if={isRequestInPaymentProgress}>
            <Button variant={ButtonVariant.GHOST_WARNING} onClick={openDeleteUserRequestModal}>
              <Icon name="delete" />
              Видалити запит
            </Button>
          </ConditionView>,

          <ConditionView key="finish-payment" if={isRequestInPaymentProgress}>
            <LinkAsButton
              variant={ButtonVariant.GHOST}
              to={ROUTES_BY_SERVICE_CODE[userRequest.service_code]}
              state={{
                continuePayment: true,
                request_id: userRequest.id,
              }}
            >
              <Icon name="payment" />
              Завершити оплату
            </LinkAsButton>
          </ConditionView>,

          ...(userRequest.artifacts || []).map((artifactType) => {
            const isArtifactExtendedResult = isArtifactExtended(artifactType);

            return (
              <React.Fragment key={artifactType}>
                <ConditionView if={isArtifactExtendedResult}>
                  <UserRequestExtendedArtifactModal
                    userRequest={userRequest}
                    artifactType={artifactType as keyof UserRequest['artifacts_extend']}
                    isModalOpen={isExtendedArtifactModalOpen}
                    closeModal={closeExtendedArtifactModal}
                  />
                </ConditionView>

                <DownloadButton
                  type={artifactType}
                  iconName={isArtifactExtendedResult ? null : 'download'}
                  buttonVariant={isArtifactExtendedResult ? ButtonVariant.PRIMARY : ButtonVariant.GHOST}
                  serviceCode={userRequest.service_code}
                  disabled={isArtifactLoading}
                  onClick={async (): Promise<void> => {
                    if (isArtifactExtendedResult) {
                      openExtendedArtifactModal();
                    } else {
                      downloadArtifact(artifactType, userRequest.service_code);
                    }
                  }}
                />
              </React.Fragment>
            );
          }),
          <ConditionView key="redelivery" if={userRequest.is_redelivery_available}>
            <ConditionView
              if={userRequest.has_unpaid_redelivery}
              else={
                <LinkAsButton
                  to={ROUTES_BY_SERVICE_CODE[SERVICE_CODES.EXTRACT_REDELIVERY]}
                  state={{ requestId: userRequest.id }}
                >
                  Замовити повторну доставку
                </LinkAsButton>
              }
            >
              <LinkAsButton
                variant={ButtonVariant.GHOST}
                to={ROUTES_BY_SERVICE_CODE[SERVICE_CODES.EXTRACT_REDELIVERY]}
                state={{ request_id: userRequest.id, continuePayment: true }}
              >
                <Icon name="payment" />
                Оплатити повторну доставку
              </LinkAsButton>
            </ConditionView>
          </ConditionView>,
          <ConditionView
            key="reapplication"
            if={
              userRequest.is_reapplication_available &&
              Boolean(userRequest.reapplication_status) &&
              userRequest.reapplication_status !== 'AWARD'
            }
          >
            <ConditionView
              if={userRequest.reapplication_status === 'NEW'}
              else={
                <LinkAsButton
                  variant={ButtonVariant.PRIMARY}
                  to={ROUTES_BY_SERVICE_CODE[SERVICE_CODES.REAPPLICATION]}
                  state={{ subject_id: userRequest.decision_id }}
                >
                  Продовжити чернетку
                </LinkAsButton>
              }
            >
              <LinkAsButton
                variant={ButtonVariant.PRIMARY}
                to={ROUTES_BY_SERVICE_CODE[SERVICE_CODES.REAPPLICATION]}
                state={{ subject_id: userRequest.decision_id }}
              >
                Поновити рух заяви
              </LinkAsButton>
            </ConditionView>
          </ConditionView>,
        ]}
      />
    </>
  );
};
